<template lang="pug">
div
  v-expand-transition
    grid-ex-table-edit-bar(
      v-if="selection.length"
      v-bind="$props"
      :actions="dataActions"
      @input="value => $emit('input', value)"
      @refresh="$emit('refresh')"
      @perform="opts => $emit('perform', opts)"
      @clearSelection="$emit('select', [])"
    )

  v-expand-transition
    grid-ex-table-action-bar(
      v-if="!selection.length"
      v-bind="$props"
      :actions="pageActions"
      @input="value => $emit('input', value)"
      @refresh="$emit('refresh')"
      @perform="opts => $emit('perform', opts)"
      @setAutoRefresh="value => $emit('setAutoRefresh', value)"
      @exportToFile="openExportDialog"
    )

  //- GRADE de dados
  grid-ex-table-data.x-data-table(
    v-bind="$props"
    :value="selection"
    :set="set"
    :summary="summary"
    :showSelect="showSelect"
    :row-actions="rowActions"
    :cell-actions="cellActions"
    :height="gridHeight"
    @input="value => $emit('select', value)"
    @refresh="$emit('refresh')"
    @perform="opts => $emit('perform', opts)"
  )

  v-dialog(v-model="exportState.dialog" max-width="650px" class="elevation-24")
    v-card
      v-card-title Exportar dados para {{ exportState.format === 'excel' ? 'Excel' : exportState.format.toUpperCase() }}
      v-card-subtitle.pl-5.pb-0 Opções de exportação de arquivo.
      v-card-text
        v-container.pa-0
          v-row.ma-0.pa-0
            v-col.d-flex.align-end.ma-0.pa-0.py-0(cols="12")
              .caption.pb-1.align-self-end Selecione as colunas a serem exportadas
              v-spacer
              v-tooltip(top)
                template(v-slot:activator="{ on, attrs }")
                  v-btn.align-self-end(v-bind="attrs" v-on="on" icon @click="selectAllExportHeaders")
                    v-icon mdi-select-all
                span Selecionar todas as colunas
              v-tooltip(top)
                template(v-slot:activator="{ on, attrs }")
                  v-btn.align-self-end(v-bind="attrs" v-on="on" @click="selectNoneExportHeaders" icon)
                    v-icon mdi-select-off
                span Desmarcar todas as colunas
              v-tooltip(top)
                template(v-slot:activator="{ on, attrs }")
                  v-btn.align-self-end(v-bind="attrs" v-on="on" icon @click="toggleSelectionExportHeaders")
                    v-icon mdi-swap-horizontal
                span Alternar a seleção de colunas

          v-row.ma-0.pa-0(style="max-height: 300px; overflow-y: auto; border: 1px solid #6666; border-radius: 4px;")
            v-col.ma-0.pa-0.px-1(cols="12" v-for="header in exportState.exportHeaders" :key="header.name")
              v-checkbox.ma-0.pa-0.my-1(
                v-model="exportState.selectedColumns"
                :label="header.title"
                :value="header.name"
                dense
                hide-details
              )
      v-card-actions
        v-text-field(
          v-model="exportState.outputFileName"
          label="Nome do arquivo"
          dense
          hide-details
          outlined
        )
        v-spacer
        v-tooltip(top)
          template(v-slot:activator="{ on, attrs }")
            v-btn.ml-2.accent(
              v-bind="attrs"
              v-on="on"
              @click="exportToFile"
              :disabled="!exportState.dialog || exportState.exporting"
            )
              | {{ exportState.exporting ? 'Exportando...' : 'Exportar' }}
          span Exporta os dados da página para um arquivo do tipo {{ exportState.format === 'excel' ? 'Excel' : exportState.format.toUpperCase() }}
        v-tooltip(top)
          template(v-slot:activator="{ on, attrs }")
            v-btn.ml-2.primary(
              v-bind="attrs"
              v-on="on"
              @click="exportState.dialog = false"
              :disabled="!exportState.dialog"
            )
              | Cancelar
          span Cancela a exportação

  v-snackbar(
    v-model="notificationState.shown" :timeout="2000"
  )
    span(:class="notificationState.style ? `${notificationState.style}--text` : ''") {{ notificationState.message }}

</template>

<script>
import GridExTableActionBar from './GridExTableActionBar'
import GridExTableEditBar from './GridExTableEditBar'
import GridExTableData from './GridExTableData'
import { hasRel } from "@/helpers/rels"
import { mergeActions } from '@/helpers/compatibility/gridex'
import { exportToExcel, exportToCsv } from '@/helpers/exporters'

export default {
  components: {
    GridExTableActionBar,
    GridExTableEditBar,
    GridExTableData,
  },

  props: {
    site: String,
    eref: String,
    form: Object,
    data: Object,
    entity: Object,

    /**
     * Conjunto dos dados exibidos na grade.
     * @type {Keep.Paper.Design.Set}
     *
     * @example
     *    O valor de um campo pode ser um literal ou um objeto contendo o campo `value` e opções
     *    extras, como um array de links relacionados.
     *
     *    {
     *      "headers": [
     *        { "name": "@row", "title": "Linha", "hidden": true },
     *        { "name": "colunaString", "title": "Coluna String", "dataType": "String" },
     *        { "name": "colunaNumero", "title": "Coluna Número", "dataType": "Number" },
     *        { "name": "colunaBoolean", "title": "Coluna Boolean", "dataType": "Boolean" },
     *        { "name": "colunaDateTime", "title": "Coluna DateTime", "dataType": "DateTime" },
     *        { "name": "colunaTexto", "title": "Coluna Texto", "dataType": "String" },
     *        { "name": "colunaInteiro", "title": "Coluna Inteiro", "dataType": "Integer" },
     *        { "name": "colunaDecimal", "title": "Coluna Decimal", "dataType": "Decimal" },
     *        { "name": "colunaShort", "title": "Coluna Short", "dataType": "Short" },
     *        { "name": "colunaLong", "title": "Coluna Long", "dataType": "Long" },
     *        { "name": "colunaMoeda", "title": "Coluna Moeda", "dataType": "Currency" },
     *        { "name": "colunaData", "title": "Coluna Data", "dataType": "Date" },
     *        { "name": "colunaHora", "title": "Coluna Hora", "dataType": "Time" },
     *        { "name": "colunaTimeSpan", "title": "Coluna TimeSpan", "dataType": "TimeSpan" }
     *      ],
     *      "rows": [
     *        {
     *          "@row": { "value": 1, "links": { eref: "ExemploDeLink", ... },
     *          "colunaString": "Exemplo String 1",
     *          "colunaNumero": 123.45,
     *          "colunaBoolean": true,
     *          "colunaDateTime": "2024-08-04T10:00:00Z",
     *          "colunaTexto": "Exemplo Texto 1",
     *          "colunaInteiro": 100,
     *          "colunaDecimal": 123.456,
     *          "colunaShort": 12,
     *          "colunaLong": 1234567890,
     *          "colunaMoeda": 99.99,
     *          "colunaData": "2024-08-04",
     *          "colunaHora": "10:00:00",
     *          "colunaTimeSpan": "10:00:00"
     *        },
     *        {
     *          "@row": { "value": 2, "links": { eref: "ExemploDeLink", ... },
     *          "colunaString": "Exemplo String 2",
     *          "colunaNumero": 678.90,
     *          "colunaBoolean": false,
     *          "colunaDateTime": "2024-08-05T11:00:00Z",
     *          "colunaTexto": "Exemplo Texto 2",
     *          "colunaInteiro": 200,
     *          "colunaDecimal": 789.012,
     *          "colunaShort": 34,
     *          "colunaLong": 9876543210,
     *          "colunaMoeda": 199.99,
     *          "colunaData": "2024-08-05",
     *          "colunaHora": "11:00:00",
     *          "colunaTimeSpan": "11:00:00"
     *        }
     *      ]
     *    }
     */
    set: Object,

    busy: Boolean,

    /**
     * Visibilidade do filtro, alternada por este botão.
     */
    value: Boolean,

    /**
     * Formulário de filtro.
     * @type {Keep.Paper.Design.Form}
     */
    filter: Object,

    /**
     * Coleção dos registros selecionados na grade.
     */
    selection: Array,

    /**
     * Tempo de atualização automática da página.
     */
    autoRefresh: Number,
  },

  data: () => ({
    notificationState: {
      shown: false,
      message: null,
      style: null // null, 'success', 'error', 'warning', 'info'
    },
    exportState: {
      dialog: false,
      exporting: false,
      selectedColumns: [],
      exportHeaders: [],
      format: '',
      outputFileName: '',
      dataset: {}
    }
  }),

  computed: {
    dataSet() {
      return this.entity?.dataSet || {}
    },

    sets() {
      return this.dataSet.sets || []
    },

    rows() {
      return this.set?.rows || []
    },

    summary() {
      // Obtendo o primeiro dataset com relacionamento "Summary".
      const set = this.sets.find(set => hasRel(set, 'Summary'))
      return set?.rows?.length ? set : null
    },

    gridHeight() {
      return this.summary ? 'calc(100vh - 214px - 42px)' : 'calc(100vh - 220px)'
    },

    /**
     * Este método processa as ações e as organiza em várias categorias e escopos.
     * As categorias são:
     * - page - Ações de página
     * - data - Ações do conjunto de dados selecionados na página.
     * - row - Ações executadas no registro sem a necessidade de seleção.
     *
     * @returns {Object} - Uma estrutura de ações organizada por categorias e escopos.
     * @example
     * {
     *   page: {
     *     menu: {
     *       all: [...], // Todas as ações da página
     *       main: [...], // Ações principais da página
     *       side: [...], // Ações secundárias da página
     *       rest: [...], // Outras ações da página
     *       categories: [
     *         { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *         { name: 'Category2', actions: [...] },
     *         // ...
     *       ]
     *     },
     *     pins: {
     *       all: [...], // Todas as ações fixadas da página
     *       main: [...], // Ações principais fixadas da página
     *       side: [...], // Ações secundárias fixadas da página
     *       rest: [...], // Outras ações fixadas da página
     *       categories: [
     *         { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *         { name: 'Category2', actions: [...] },
     *         // ...
     *       ]
     *     },
     *     states: {
     *       all: [...], // Todos os monitores de estado da página
     *       main: [...], // Monitores de estado da página
     *       side: [...], // Monitores de estado secundários da página
     *       rest: [...], // Outros monitores de estado da página
     *       categories: [
     *         { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *         { name: 'Category2', actions: [...] },
     *         // ...
     *       ]
     *     }
     *   },
     *   data: { /* ... * / }, // Estrutura semelhante para as ações de dados
     *   row: { /* ... * / }  // Estrutura semelhante para as ações de linha
     * }
     */
    actions() {
      return this.entity.actions
    },

    cellActions() {
      return this.actions.cell
    },

    rowActions() {
      return this.actions.row
    },

    pageActions() {
      return this.actions.page
    },

    dataActions() {
      // Verificando a existência e o conteúdo de links
      const links = this.dataSet.links
      if (links?.length > 0) {
        return mergeActions(this.actions.data, links)
      }
      return this.actions.data
    },

    showSelect() {
      return !!this.dataActions?.menu?.all?.length
    },

    exportHeaders() {
      return this.set.headers
        .filter(header => header.name !== '@row')
        .map(header => ({
          name: header.name,
          title: header.title || header.name
        }));
    },
  },

  methods: {
    notify(message, style) {
      this.notificationState.message = message
      this.notificationState.style = style
      this.notificationState.shown = true
    },

    selectAllExportHeaders() {
      this.exportState.selectedColumns = this.exportState.exportHeaders.map(header => header.name)
    },

    selectNoneExportHeaders() {
      this.exportState.selectedColumns = []
    },

    toggleSelectionExportHeaders() {
      const state = this.exportState
      const unselectedHeaders = state.exportHeaders.filter(
          header => !state.selectedColumns.includes(header.name)
      ).map(header => header.name);
      state.selectedColumns = unselectedHeaders;
    },

    openExportDialog(opts) {
      const format = opts.format || 'excel';
      const dataset = { set: this.set };

      if (!dataset || dataset.set.rows.length === 0) {
        this.notify('Não há dados para exportar.', 'warning');
        return;
      }

      const filename = this.entity?.title || this.entity?.name || this.entity?.eref || 'dados';
      let extension;
      switch (format) {
        case 'excel':
          extension = 'xlsx';
          break;
        case 'csv':
          extension = 'csv';
          break;
        default:
          this.notify(`Formato de exportação não suportado: ${format}`, 'warning')
          return;
      }
      this.exportState.outputFileName = `${filename}.${extension}`;

      // Atualizar as colunas disponíveis no diálogo
      const availableHeaders = this.set.headers
        .filter(header =>
          header.name !== '@row' &&
          (!header.hidden || header.allowExport) &&
          !(header.rel && header.rel.includes('Icon'))
        )
        .map(header => ({
          name: header.name,
          title: header.title || header.name
        }));
      this.exportState.exportHeaders = availableHeaders;

      // Atualizar as colunas selecionadas, mantendo a seleção anterior se existir
      const previousSelection = new Set(this.exportState.selectedColumns || []);
      const updatedSelection = availableHeaders.filter(
          header => previousSelection.has(header.name)).map(header => header.name);

      // Se não houver seleção ou estiver vazia, selecionar todas as colunas
      if (updatedSelection.length === 0) {
        this.exportState.selectedColumns = availableHeaders.map(header => header.name);
      } else {
        this.exportState.selectedColumns = updatedSelection;
      }

      // Armazenar o formato e o dataset
      this.exportState.format = format;
      this.exportState.dataset = dataset;

      // Mostrar o diálogo
      this.exportState.dialog = true;
    },

    exportToFile() {
      this.exportState.exporting = true

      const dataset = this.exportState.dataset
      const format = this.exportState.format
      const filename = this.exportState.outputFileName

      dataset.options = { includes: this.exportState.selectedColumns }

      setTimeout(() => {
        try {
          if (format === 'excel') {
            exportToExcel(dataset, filename)
            this.notify('Exportação para Excel realizada com sucesso.', 'success')
          } else if (format === 'csv') {
            exportToCsv(dataset, filename)
            this.notify('Exportação para CSV realizada com sucesso.', 'success')
          } else {
            this.notify('Formato de exportação não suportado.', 'error')
          }
        } catch (error) {
          console.error(error)
          this.notify('Erro ao exportar os dados.', 'error')
        } finally {
          this.exportState.dialog = false
          this.exportState.exporting = false
        }
      }, 500)
    },
  },
}
</script>